import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { Loader2, ArrowRight, Sparkles, CheckCircle2, AlertTriangle } from "lucide-react";

// Client-side preview scoring — deterministic-ish heuristic for a fast "try me" experience.
// We label this as a PREVIEW; the full GPT-5.2 audit is one click away.
const ACTION_VERBS = ["led", "drove", "shipped", "built", "scaled", "reduced", "increased", "launched", "designed", "architected", "delivered", "owned", "spearheaded", "improved", "automated", "optimized", "engineered", "founded", "managed", "grew", "negotiated", "trained", "mentored", "secured", "implemented"];
const WEAK_WORDS = ["responsible for", "worked on", "helped with", "duties included", "team player", "passionate", "results-oriented", "go-getter", "synergy"];
const BUZZWORDS = ["python", "javascript", "react", "node", "sql", "aws", "docker", "kubernetes", "typescript", "go", "rust", "product", "strategy", "growth", "marketing", "sales", "design", "figma", "saas", "b2b", "fintech", "ml", "ai", "data", "analytics"];

function scorePreview(text) {
  const t = text.trim();
  const lower = t.toLowerCase();
  const words = t.split(/\s+/).length;

  let impact = 30;
  let brevity = 50;
  let style = 50;
  let skills = 30;

  // Word count band — too short/long both penalize impact
  if (words >= 30) impact += 10;
  if (words >= 80) impact += 5;
  brevity += words < 40 ? 10 : words < 120 ? 20 : words < 250 ? 10 : -10;

  // Action verbs
  const verbHits = ACTION_VERBS.filter((v) => lower.includes(v)).length;
  impact += Math.min(verbHits * 4, 28);
  style += Math.min(verbHits * 2, 12);

  // Numbers / quantification
  const numbers = (t.match(/\b\d+(?:\.\d+)?%?\b/g) || []).length;
  impact += Math.min(numbers * 3, 18);
  style += Math.min(numbers * 1.5, 8);

  // Weak phrases
  const weakHits = WEAK_WORDS.filter((w) => lower.includes(w)).length;
  style -= weakHits * 8;
  impact -= weakHits * 4;

  // Skill-keyword coverage
  const skillHits = BUZZWORDS.filter((b) => lower.includes(b)).length;
  skills += Math.min(skillHits * 6, 50);

  // Clamp
  const clamp = (n) => Math.max(20, Math.min(95, Math.round(n)));
  impact = clamp(impact);
  brevity = clamp(brevity);
  style = clamp(style);
  skills = clamp(skills);

  const overall = clamp((impact + brevity + style + skills) / 4);
  const ats = clamp(overall - 4 + Math.min(skillHits * 2, 10));

  // Build a couple of preview insights tied to what we saw
  const wins = [];
  const gaps = [];
  if (verbHits >= 3) wins.push(`Strong action verbs detected (${verbHits} found)`);
  if (numbers >= 2) wins.push(`Quantified impact present (${numbers} numbers)`);
  if (skillHits >= 3) wins.push(`Good keyword coverage (${skillHits} skills matched)`);
  if (verbHits < 2) gaps.push("Lead with stronger action verbs (Led, Drove, Shipped)");
  if (numbers < 2) gaps.push("Add metrics — recruiters scan for % and $");
  if (weakHits > 0) gaps.push(`Cut ${weakHits} weak phrase${weakHits > 1 ? "s" : ""} (e.g. "responsible for")`);
  if (skillHits < 2) gaps.push("Layer in role-specific skills");
  while (wins.length < 1) wins.push("Concise structure scans cleanly");
  while (gaps.length < 2) gaps.push("Tighten brevity — aim for 12-18 words/bullet");

  return { overall, ats, impact, brevity, style, skills, wins: wins.slice(0, 3), gaps: gaps.slice(0, 3) };
}

function useAnimatedNumber(target, duration = 1100, trigger = true) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!trigger) { setValue(0); return; }
    let raf;
    const start = performance.now();
    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      setValue(Math.round(target * eased));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration, trigger]);
  return value;
}

function MiniRing({ value, size = 120 }) {
  const v = Math.max(0, Math.min(100, value || 0));
  const r = 50;
  const c = 2 * Math.PI * r;
  const offset = c - (v / 100) * c;
  const color = v >= 80 ? "#007A5E" : v >= 60 ? "#DC1C22" : v >= 40 ? "#FFBF00" : "#FF2A00";
  return (
    <div className="relative inline-flex items-center justify-center" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} stroke="#F4F4F5" strokeWidth="8" fill="none" />
        <circle
          cx={size / 2} cy={size / 2} r={r}
          stroke={color} strokeWidth="8" fill="none"
          strokeDasharray={c} strokeDashoffset={offset}
          strokeLinecap="square"
          style={{ transition: "stroke-dashoffset 1.1s cubic-bezier(0.22, 1, 0.36, 1)" }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <div className="font-display font-extrabold text-3xl tabular-nums" style={{ color }}>{v}</div>
        <div className="overline mt-0.5" style={{ fontSize: "9px" }}>SCORE</div>
      </div>
    </div>
  );
}

const SAMPLE = `Senior Software Engineer at Acme (2020-Present)
Led migration of legacy monolith to Kubernetes, reducing p95 latency by 38% and cutting infra cost by $240k/year.
Shipped 12 production features in TypeScript + React serving 2.3M users.
Mentored 4 junior engineers; 2 promoted within 12 months.`;

export default function LiveDemoWidget() {
  const [text, setText] = useState("");
  const [state, setState] = useState("idle"); // idle | scoring | done
  const [result, setResult] = useState(null);
  const animatedOverall = useAnimatedNumber(result?.overall || 0, 1100, state === "done");
  const animatedAts = useAnimatedNumber(result?.ats || 0, 1100, state === "done");

  const runPreview = () => {
    const t = text.trim();
    if (t.length < 30) {
      // Use sample if user hasn't pasted much
      setText(SAMPLE);
      setState("scoring");
      setTimeout(() => { setResult(scorePreview(SAMPLE)); setState("done"); }, 1100);
      return;
    }
    setState("scoring");
    setTimeout(() => { setResult(scorePreview(t)); setState("done"); }, 1200);
  };

  const reset = () => { setState("idle"); setResult(null); };

  return (
    <section className="border-b border-zinc-200 bg-zinc-50" data-testid="live-demo">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10 lg:py-14">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-12">
          <div className="lg:col-span-6">
            <div className="overline mb-4">◷ TRY IT NOW · NO SIGNUP</div>
            <h2 className="font-display font-extrabold text-4xl lg:text-5xl tracking-tight leading-[1.0] mb-6">
              Paste a few bullets.<br />Get scored in <span className="text-[#DC1C22]">2 seconds</span>.
            </h2>
            <p className="text-zinc-600 text-lg leading-relaxed max-w-xl">
              This is an instant preview using heuristics. The full audit uses GPT-5.2 to give you line-by-line recruiter-grade feedback. Free first audit — no card required.
            </p>
          </div>
          <div className="lg:col-span-6 flex items-center justify-end">
            <div className="text-xs text-zinc-500 max-w-sm">
              <span className="font-mono">●</span> Heuristic preview · For the real audit (line-by-line GPT-5.2 rewrite suggestions) hit <Link to="/evaluate" className="underline text-[#DC1C22]">Run full audit</Link>.
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-px bg-zinc-200 border border-zinc-200 bg-white">
          {/* Left: input */}
          <div className="lg:col-span-7 bg-white p-6 lg:p-8">
            <div className="flex items-center justify-between mb-4">
              <div className="overline">01 · PASTE A RESUME SNIPPET</div>
              <button
                onClick={() => setText(SAMPLE)}
                data-testid="use-sample"
                className="text-xs text-zinc-500 hover:text-[#DC1C22] underline"
              >
                Use sample
              </button>
            </div>
            <textarea
              value={text}
              onChange={(e) => { setText(e.target.value); if (state === "done") setState("idle"); }}
              placeholder="Paste 2-5 bullets from your resume. We'll score in 2 seconds — no signup."
              rows={9}
              data-testid="demo-textarea"
              className="w-full font-mono text-sm border border-zinc-200 p-4 focus:outline-none focus:ring-2 focus:ring-blue-300 resize-y bg-white"
            />
            <div className="flex items-center justify-between mt-4">
              <span className="text-xs text-zinc-400 font-mono">{text.length} chars</span>
              <div className="flex items-center gap-2">
                {state === "done" && (
                  <button onClick={reset} className="text-xs text-zinc-500 hover:text-zinc-900 px-3 py-2">
                    Clear
                  </button>
                )}
                <button
                  onClick={runPreview}
                  disabled={state === "scoring"}
                  data-testid="demo-score-btn"
                  className="btn-sharp bg-[#DC1C22] hover:bg-[#A8141A] text-white px-5 py-2.5 disabled:bg-zinc-300"
                >
                  {state === "scoring" ? (<><Loader2 className="w-4 h-4 animate-spin" /> Scoring...</>) :
                   state === "done" ? (<><Sparkles className="w-4 h-4" /> Re-score</>) :
                   (<>Score now <ArrowRight className="w-4 h-4" /></>)}
                </button>
              </div>
            </div>
          </div>

          {/* Right: preview output */}
          <div className="lg:col-span-5 bg-white p-6 lg:p-8 flex flex-col">
            {state === "idle" && (
              <div className="flex-1 flex flex-col items-center justify-center text-center" data-testid="demo-empty">
                <div className="w-16 h-16 border-2 border-dashed border-zinc-300 inline-flex items-center justify-center mb-4">
                  <Sparkles className="w-6 h-6 text-zinc-400" strokeWidth={1.5} />
                </div>
                <p className="text-zinc-500 text-sm max-w-xs">
                  Your instant preview will appear here.
                </p>
              </div>
            )}

            {state === "scoring" && (
              <div className="flex-1 flex flex-col items-center justify-center" data-testid="demo-scoring">
                <Loader2 className="w-7 h-7 animate-spin text-[#DC1C22] mb-4" />
                <div className="overline">ANALYZING...</div>
                <div className="text-xs text-zinc-500 mt-1 font-mono">heuristic engine · 1.1s</div>
              </div>
            )}

            {state === "done" && result && (
              <div className="flex-1 flex flex-col fade-up" data-testid="demo-result">
                <div className="flex items-center gap-4 mb-5">
                  <MiniRing value={animatedOverall} size={104} />
                  <div className="flex-1">
                    <div className="overline mb-1">PREVIEW SCORE</div>
                    <div className="font-display font-bold text-sm text-zinc-700">
                      ATS · <span className="font-mono tabular-nums">{animatedAts}/100</span>
                    </div>
                    <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-xs font-mono text-zinc-500">
                      <span>Impact <span className="text-zinc-900 tabular-nums">{result.impact}</span></span>
                      <span>Brevity <span className="text-zinc-900 tabular-nums">{result.brevity}</span></span>
                      <span>Style <span className="text-zinc-900 tabular-nums">{result.style}</span></span>
                      <span>Skills <span className="text-zinc-900 tabular-nums">{result.skills}</span></span>
                    </div>
                  </div>
                </div>

                <div className="space-y-2 mb-4">
                  {result.wins.slice(0, 2).map((w, i) => (
                    <div key={i} className="flex items-start gap-2 text-xs">
                      <CheckCircle2 className="w-3.5 h-3.5 text-[#007A5E] mt-0.5 shrink-0" strokeWidth={2.5} />
                      <span className="text-zinc-700">{w}</span>
                    </div>
                  ))}
                  {result.gaps.slice(0, 2).map((g, i) => (
                    <div key={i} className="flex items-start gap-2 text-xs">
                      <AlertTriangle className="w-3.5 h-3.5 text-[#FF2A00] mt-0.5 shrink-0" strokeWidth={2.5} />
                      <span className="text-zinc-700">{g}</span>
                    </div>
                  ))}
                </div>

                <Link
                  to="/evaluate"
                  data-testid="demo-cta-full-audit"
                  className="btn-sharp bg-zinc-900 hover:bg-[#DC1C22] text-white w-full mt-auto"
                >
                  Get the full GPT-5.2 audit <ArrowRight className="w-4 h-4" />
                </Link>
                <p className="text-[10px] text-zinc-400 text-center mt-2 font-mono uppercase tracking-wider">
                  Heuristic preview · Full audit gives line-by-line rewrites
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
